import * as React from 'react';
import { Dialog, DialogContent, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { X } from '@phosphor-icons/react';
import { ImportResultsPanel } from './ImportResultsPanel';
import { ImportProgressBar } from './ImportProgressBar';

type ImportProgress = { done: number; total: number; imported: number; rejected: number };

interface ImportStatusModalProps {
  open: boolean;
  onClose: () => void;
  /** Live import progress (SSE) — shown while an import is running. */
  progress: ImportProgress | null | undefined;
  /** Last-import result summary — shown once the import finishes. */
  result: React.ComponentProps<typeof ImportResultsPanel>['result'] | null;
  /** Clears the picked file after the user dismisses the result. */
  onResetResult: () => void;
  /** "Import new sheet" — closes this modal and opens the upload modal. */
  onUploadAnother: () => void;
}

/**
 * Import progress + results in a modal (popup with a close button). Opens after
 * "Upload sheet" is submitted (progress → results) and from the last-import
 * button on the Creators tab.
 */
export function ImportStatusModal({
  open,
  onClose,
  progress,
  result,
  onResetResult,
  onUploadAnother,
}: ImportStatusModalProps) {
  const importing = !!progress;
  const title = importing ? 'Importing creators' : 'Import results';
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent
        showCloseButton={false}
        className="flex max-h-[85vh] max-w-2xl flex-col gap-0 overflow-hidden rounded-2xl p-0 sm:max-w-2xl"
      >
        {/* Fixed — outside the scrollable body below, so a long result (many
            rejection groups) never pushes the header/close button off-screen
            or grows the dialog past the viewport. */}
        <div className="flex shrink-0 items-center justify-between border-b border-border px-6 py-4">
          <DialogTitle className="text-heading-h7 text-foreground">{title}</DialogTitle>
          <Button
            variant="ghost"
            size="icon"
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="size-9 rounded-lg border border-border text-muted-foreground hover:bg-accent"
          >
            <X size={18} />
          </Button>
        </div>

        {/* min-h-0: required for a flex child to actually shrink and scroll
            instead of growing to fit its content inside a flex-col parent. */}
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {progress ? (
            <ImportProgressBar
              done={progress.done}
              total={progress.total}
              imported={progress.imported}
              rejected={progress.rejected}
            />
          ) : result ? (
            <ImportResultsPanel
              result={result}
              onReset={onResetResult}
              onUploadAnother={onUploadAnother}
            />
          ) : (
            <div className="py-8 text-center text-sm text-muted-foreground">Preparing import…</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
